import {useEffect} from 'react';

const siteUrl = 'https://versohemelslinn.it';
const siteName = 'Verso Hemelslinn';
const defaultImage = `${siteUrl}/images/copertina-verso-hemelslinn.webp`;
const defaultImageAlt =
  'Copertina del romanzo Verso Hemelslinn su un tavolo in una cabina spaziale, con lo spazio visibile dalla finestra.';
const defaultDescription =
  'Verso Hemelslinn è una space opera romantica e umanista senza alieni: un romanzo di Maurizio Feruglio tra stelle, amore, libertà e scelte morali.';

function absoluteUrl(path = '/') {
  if (path.startsWith('http')) {
    return path;
  }

  return `${siteUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let element = document.head.querySelector(`script[data-seo-jsonld="${id}"]`);

  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.setAttribute('data-seo-jsonld', id);
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const element = document.head.querySelector(`script[data-seo-jsonld="${id}"]`);

  if (element) {
    element.remove();
  }
}

function Seo({
  title,
  description = defaultDescription,
  path = '/',
  image = defaultImage,
  imageAlt = defaultImageAlt,
  type = 'website',
  noindex = false,
  jsonLd,
}) {
  useEffect(() => {
    const pageTitle = title ? (title.includes(siteName) ? title : `${title} | ${siteName}`) : siteName;
    const canonical = absoluteUrl(path);
    const imageUrl = absoluteUrl(image);

    document.documentElement.lang = 'it';
    document.title = pageTitle;

    upsertMeta('meta[name="description"]', {name: 'description', content: description});
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex,follow' : 'index,follow,max-image-preview:large',
    });

    upsertLink('canonical', canonical);

    upsertMeta('meta[property="og:locale"]', {property: 'og:locale', content: 'it_IT'});
    upsertMeta('meta[property="og:type"]', {property: 'og:type', content: type});
    upsertMeta('meta[property="og:site_name"]', {property: 'og:site_name', content: siteName});
    upsertMeta('meta[property="og:title"]', {property: 'og:title', content: pageTitle});
    upsertMeta('meta[property="og:description"]', {property: 'og:description', content: description});
    upsertMeta('meta[property="og:url"]', {property: 'og:url', content: canonical});
    upsertMeta('meta[property="og:image"]', {property: 'og:image', content: imageUrl});
    upsertMeta('meta[property="og:image:alt"]', {property: 'og:image:alt', content: imageAlt});

    upsertMeta('meta[name="twitter:card"]', {name: 'twitter:card', content: 'summary_large_image'});
    upsertMeta('meta[name="twitter:title"]', {name: 'twitter:title', content: pageTitle});
    upsertMeta('meta[name="twitter:description"]', {name: 'twitter:description', content: description});
    upsertMeta('meta[name="twitter:image"]', {name: 'twitter:image', content: imageUrl});
    upsertMeta('meta[name="twitter:image:alt"]', {name: 'twitter:image:alt', content: imageAlt});

    upsertJsonLd('website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: siteName,
      inLanguage: 'it-IT',
      publisher: {
        '@type': 'Organization',
        name: 'Eliosfera Editrice',
      },
    });

    if (jsonLd) {
      upsertJsonLd('page', jsonLd);
    } else {
      removeJsonLd('page');
    }
  }, [title, description, path, image, imageAlt, type, noindex, jsonLd]);

  return null;
}

export {siteUrl, siteName, defaultDescription, defaultImage, defaultImageAlt};
export default Seo;
